import { doc, getDocFromServer, onSnapshot, Unsubscribe } from "firebase/firestore";
import { GetOneParams, GetOneResult, Identifier } from "react-admin";
import { firestore } from "../firebase";
import { PostSchema, ThreadSchema, type Post, type Thread } from "../model/Thread.js";
import { AbstractDataProvider } from "./AbstractDataProvider";
import { grants } from "../backoffice.access_control";

class ThreadsProvider extends AbstractDataProvider<Thread> {
  private _snapshotPromiseCache: Map<Identifier, Promise<Thread> | undefined> = new Map();
  private _unsubscribe: Unsubscribe | undefined;
  private readonly _onUpdateListeners: Array<() => void> = [];

  constructor() {
    super();
  }

  async getOne(_: string, { id }: GetOneParams<Thread>): Promise<GetOneResult<Thread>> {
    const thread = await this.fetchIfNeeded(id);

    thread.sortedPosts = thread.sortedPosts.map((post) => {
      thread.posts[post.id] = _buildPostId(post);
      return _buildPostId(post);
    });
    return { data: thread };
  }

  fetchIfNeeded(recordId: Identifier): Promise<Thread> {
    if (this._snapshotPromiseCache.has(recordId)) {
      return this._snapshotPromiseCache.get(recordId)!;
    }
    return this.fetch(recordId);
  }

  fetch(threadId: Identifier): Promise<Thread> {
    const document = doc(firestore, `threads/${threadId}`);
    this._unsubscribe?.();
    this._unsubscribe = undefined;
    const fetchThreadPromise = getDocFromServer(document).then((snapshot) => {
      this._unsubscribe = onSnapshot(document, async (snapshot) => {
        const updatedThread = ThreadSchema.parse(snapshot.data());
        updatedThread.sortedPosts = updatedThread.sortedPosts.filter((post) => {
          const hideDeletedPost = post.deleted && !grants.has("Post:viewDeleted");
          if (hideDeletedPost) {
            delete updatedThread.posts[post.id];
            return false;
          }
          return true;
        });
        this._snapshotPromiseCache.set(threadId, Promise.resolve(updatedThread));
        console.info(
          `Retrieved new snapshot on thread ${updatedThread.id} with ${Object.keys(updatedThread.posts).length} post(s)`,
        );
        for (const listener of this._onUpdateListeners) {
          try {
            listener();
          } catch (error) {
            console.error("listener failed", error);
          }
        }
      });
      return ThreadSchema.parse(snapshot.data());
    });
    this._snapshotPromiseCache.set(threadId, fetchThreadPromise);
    return fetchThreadPromise;
  }

  onUpdate(listener: () => any): () => void {
    if (this._onUpdateListeners.indexOf(listener) >= 0) {
      throw new Error("listener already registered");
    }
    this._onUpdateListeners.push(listener);
    return () => {
      const i = this._onUpdateListeners.indexOf(listener);
      if (i < 0) {
        throw new Error("listener already unregistered");
      }
      this._onUpdateListeners.splice(i, 1);
    };
  }
}

export const threadsProvider = new ThreadsProvider();

function _buildPostId(post: Post): Post {
  return PostSchema.parse({
    ...post,
    id: post.id.includes(":") ? post.id : `${post.threadId}:${post.id}`,
  });
}
